import { useState, useEffect, useCallback } from 'react';
import {
  saveFilter,
  saveSearchQuery,
  getFilters,
  deleteFilter,
  updateFilter,
} from '../services/savedFiltersService';
import { userAppContext } from '../context/Context';
import { UserContext, SavedFilter } from '../types';

export const useSavedFilters = (isEvidence = false) => {
  const { isAuthenticated } = userAppContext() as unknown as UserContext;
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMigrated, setHasMigrated] = useState(false);

  const loadFilters = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getFilters();
      const userFilters = response.filters || [];

      const filteredByType = userFilters.filter(f => f.isEvidence === isEvidence);

      setSavedFilters(filteredByType);
    } catch (err) {
      const typedError = err as { message: string };
      setError(typedError.message || 'Failed to load filters');
      console.error('Error loading filters:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isEvidence]);

  const handleSaveFilter = useCallback(
    async (name, filters, searchQuery = null) => {
      if (!isAuthenticated) {
        setError('You must be logged in to save filters');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await saveFilter(name, filters, isEvidence, searchQuery);
        await loadFilters();
        return response.filter;
      } catch (err) {
        const typedError = err as { message: string };
        setError(typedError.message || 'Failed to save filter');
        console.error('Error saving filter:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, isEvidence, loadFilters],
  );

  const handleSaveSearchQuery = useCallback(
    async (name, searchQuery) => {
      if (!isAuthenticated) {
        setError('You must be logged in to save search queries');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await saveSearchQuery(name, searchQuery, isEvidence);
        await loadFilters();
        return response.filter;
      } catch (err) {
        const typedError = err as { message: string };
        setError(typedError.message || 'Failed to save search query');
        console.error('Error saving search query:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, isEvidence, loadFilters],
  );

  const handleDeleteFilter = useCallback(
    async filterId => {
      if (!isAuthenticated) return;

      setIsLoading(true);
      setError(null);

      try {
        await deleteFilter(filterId);
        setSavedFilters(prev => prev.filter(f => f.filterId !== filterId));
      } catch (err) {
        const typedError = err as { message: string };
        setError(typedError.message || 'Failed to delete filter');
        console.error('Error deleting filter:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated],
  );

  const handleUpdateFilter = useCallback(
    async (filterId, updates) => {
      if (!isAuthenticated) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await updateFilter(filterId, updates);
        setSavedFilters(prev => prev.map(f => (f.filterId === filterId ? response.filter : f)));
        return response.filter;
      } catch (err) {
        const typedError = err as { message: string };
        setError(typedError.message || 'Failed to update filter');
        console.error('Error updating filter:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated],
  );

  const handleMigration = useCallback(async () => {
    if (!isAuthenticated || hasMigrated) return;

    const localFilters = localStorage.getItem('filters');
    if (!localFilters) {
      setHasMigrated(true);
      return;
    }

    setIsLoading(true);
    try {
      setHasMigrated(true);
      await loadFilters();
    } catch (err) {
      console.error('Migration failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, hasMigrated, loadFilters]);

  useEffect(() => {
    if (isAuthenticated) {
      loadFilters();
      handleMigration();
    }
  }, [isAuthenticated, loadFilters, handleMigration]);

  return {
    savedFilters,
    isLoading,
    error,
    saveFilter: handleSaveFilter,
    saveSearchQuery: handleSaveSearchQuery,
    deleteFilter: handleDeleteFilter,
    updateFilter: handleUpdateFilter,
    reloadFilters: loadFilters,
  };
};
